import React, { useEffect, useRef, useState } from 'react'
import { fmt } from '../api.js'
import { portal } from '../portalApi.js'
import DateRange from '../components/DateRange.jsx'
import SourceData from './SourceData.jsx'
import VendorPickerModal from '../components/VendorPickerModal.jsx'

const monthRange = (month) => {
  const [y, m] = month.split('-').map(Number)
  const last = new Date(y, m, 0).getDate()
  return [`${month}-01`, `${month}-${String(last).padStart(2, '0')}`]
}

// 증권(한국투자증권 종합계좌) 화면 — 업로드 / 원장 / 분개 3탭.
// 한투는 클로브가 거래내역을 주지 않아 원장을 API로 못 받는다. HTS에서 내려받은 엑셀 2종
// (전체거래내역 + 원천징수내역)을 올리면 서버 파서가 통화별 평면 원장으로 바꿔 저장한다.
const TABS = [
  { k: 'upload', label: '업로드' },
  { k: 'ledger', label: '원장' },
  // 예수금 흐름(원장)만 보면 MMT 매수가 «돈이 나간 것»으로 읽힌다. 자리만 바뀐 것이라
  // 보유 종목을 같이 봐야 갈린다.
  { k: 'holdings', label: '보유 종목' },
  { k: 'journal', label: '분개' },
]

function UploadPane({ onUploaded, onGoLedger }) {
  const tradeRef = useRef(null)
  const whRef = useRef(null)
  const [trade, setTrade] = useState(null)
  const [wh, setWh] = useState(null)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const [result, setResult] = useState(null)

  const submit = async () => {
    if (!trade || !wh) { setErr('거래내역과 원천징수내역 파일을 모두 선택해주세요.'); return }
    setBusy(true); setErr(''); setResult(null)
    try {
      const fd = new FormData()
      fd.append('trade_file', trade)
      fd.append('withholding_file', wh)
      const res = await fetch('/api/securities/upload', { method: 'POST', body: fd, credentials: 'same-origin' })
      const body = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(body.error || res.statusText)
      setResult(body)
      setTrade(null); setWh(null)
      if (tradeRef.current) tradeRef.current.value = ''
      if (whRef.current) whRef.current.value = ''
      onUploaded?.()
    } catch (e) { setErr(e.message) } finally { setBusy(false) }
  }

  const checks = result?.validation?.checks ?? []
  const warnings = result?.validation?.warnings ?? []
  // 판정에 안 들어가는 참고 줄(D·E)은 빼고 센다 — "3건 어긋남"처럼 읽히면 안 된다
  const 대조 = checks.filter((c) => c['참고'] !== true && c['건수'] === undefined)
  const 어긋남 = 대조.filter((c) => !c['일치']).length
  const 깨끗 = result && !warnings.length && !어긋남

  return (
    <>
      <div className="hint" style={{ marginBottom: 12 }}>
        한국투자증권 <b>홈페이지</b>에서 두 파일을 <b>같은 기간</b>으로 받아 함께 올려주세요.
        계좌는 둘 다 <code>81297462-21 (주식회사 에르모어)</code> 입니다.
        <ol className="sec-path">
          <li>
            <b>거래내역</b>
            <div className="sec-path-nav">뱅킹/청약/대출 › 조회 › 거래내역 › 계좌별거래내역 › <b>「전체거래내역」 탭</b></div>
            <div>상품유형구분 <b>전체</b> · 업무구분 <b>전체(펀드거래)</b> + <b>「CMA 자동매매내역포함」도 함께 체크</b></div>
          </li>
          <li>
            <b>원천징수내역</b>
            <div className="sec-path-nav">고객서비스 › 증명서발급 › <b>원천징수내역조회</b></div>
            <div>소득구분 <b>전체</b> · 과세유형 <b>전체</b> · 발급단위 <b>상품계좌단위</b></div>
          </li>
        </ol>
        내려받은 <code>.xls</code>는 엑셀로 변환하지 않고 그대로 올려도 됩니다.
        같은 기간을 다시 올리면 그 기간이 새 파일 내용으로 바뀝니다 — 여러 번 올려도 한 벌만 남습니다.
        <br />
        ⚠ CMA 이자입금이 통째로 빠진 적이 있습니다. 올린 뒤 아래 <b>검증</b>이 ✓ 인지 꼭 보세요 —
        거래내역과 원천징수내역이 어긋나면 거기서 잡힙니다.
      </div>

      <div className="panel" style={{ padding: 16, marginBottom: 12 }}>
        <div className="row" style={{ marginBottom: 10 }}>
          <span className="muted" style={{ width: 110 }}>거래내역</span>
          <input ref={tradeRef} type="file" accept=".xlsx,.xls" onChange={(e) => setTrade(e.target.files?.[0] ?? null)} />
        </div>
        <div className="row" style={{ marginBottom: 14 }}>
          <span className="muted" style={{ width: 110 }}>원천징수내역</span>
          <input ref={whRef} type="file" accept=".xlsx,.xls" onChange={(e) => setWh(e.target.files?.[0] ?? null)} />
        </div>
        <button className="btn" disabled={busy || !trade || !wh} onClick={submit}>
          {busy ? '업로드 및 검증 중…' : '업로드'}
        </button>
      </div>

      {err && <div className="error-box">{err}</div>}

      {result && (
        <div className="panel" style={{ padding: 16 }}>
          <div style={{ marginBottom: 10 }}>
            <b>{fmt(result.added)}건</b>
            {result.replacedRange && (
              <span className="muted">
                {' '}· {result.replacedRange[0]} ~ {result.replacedRange[1]} 구간을 새로 채웠습니다
                {result.replaced ? ` (이전 ${fmt(result.replaced)}건 교체)` : ''}
              </span>
            )}
            <span className="muted"> · 원본 거래 {fmt(result.tradeCount)}건 → 원장 {fmt(result.ledgerCount)}행</span>
          </div>
          <div style={{ marginBottom: 10, color: 깨끗 ? 'var(--accent)' : 'var(--danger)' }}>
            {깨끗
              ? `검증 ${대조.length}개 항목 전부 일치 — 잔액이 HTS와 맞습니다.`
              : `검증 ${어긋남}개 항목이 어긋났습니다${warnings.length ? ` · 경고 ${warnings.length}건` : ''} — 아래를 확인해 주세요.`}
          </div>
          {warnings.length > 0 && (
            <div className="error-box" style={{ marginBottom: 10 }}>
              {warnings.map((w, i) => <div key={i}>{w}</div>)}
            </div>
          )}
          <table>
            <thead><tr><th className="l">검증 항목</th><th>원본</th><th>계산</th><th>결과</th></tr></thead>
            <tbody>
              {checks.map((c, i) => {
                // 합계만 보여주는 줄(E)은 대조가 아니다 — '일치'라고 적으면 검증한 것처럼 읽힌다
                const 참고만 = c['참고'] === true
                  || (c['상세'] === undefined && c['원본'] === undefined && c['건수'] !== undefined)
                // 어긋난 날짜를 접어두면 "불일치"만 보이고 어디가 문제인지 알 수 없다
                const 어긋난날 = (c['상세'] || []).filter((d) => !d['일치'])
                return (
                  <React.Fragment key={i}>
                    <tr>
                      <td className="l">{c['항목']}</td>
                      <td>{c['원본'] !== undefined ? fmt(c['원본'], 2) : c['건수'] !== undefined ? `${fmt(c['건수'])}건` : ''}</td>
                      <td>{c['계산'] !== undefined ? fmt(c['계산'], 2) : c['과표금액'] !== undefined ? fmt(c['과표금액']) : ''}</td>
                      <td className={c['일치'] || 참고만 ? '' : 'c'}
                        style={{ color: 참고만 ? 'var(--muted)' : c['일치'] ? 'var(--accent)' : 'var(--danger)' }}>
                        {참고만 ? '참고' : c['일치'] ? '일치' : '불일치'}
                      </td>
                    </tr>
                    {어긋난날.length > 0 && (
                      <tr>
                        <td colSpan={4} className="l" style={{ background: 'var(--accent-soft)' }}>
                          {/* 참고 줄은 접어 둔다 — 전부 일치인데 표가 펼쳐져 있으면 문제가 있는 것처럼 읽힌다 */}
                          <details open={!참고만}>
                            <summary className="muted" style={{ cursor: 'pointer' }}>
                              날짜가 어긋난 {어긋난날.length}일 · 세액 차이 합계{' '}
                              {fmt(어긋난날.reduce((s, d) => s + d['세액차이'], 0))}원
                            </summary>
                          <div className="muted" style={{ margin: '6px 0 4px' }}>
                            아래는 두 내역의 날짜가 어긋난 날입니다 — 예수금에 이자가 붙은 날과 원천징수 거래일자가
                            하루 차이거나, 신탁 안에 쌓인 이자에 세금만 먼저 뗀 뒤(세금징수) 그 이자가 며칠 뒤
                            매도로 실현된 경우입니다. <b>합계가 0이면 날짜만 옮겨간 것이고 금액이 새거나 겹친 것은 없습니다.</b>
                          </div>
                          <table className="w-exc" style={{ margin: 0 }}>
                            <thead>
                              <tr><th>일자</th><th>과표 차이</th><th>세액 차이</th></tr>
                            </thead>
                            <tbody>
                              {어긋난날.map((d) => (
                                <tr key={d['일자']}>
                                  <td className="c">{d['일자']}</td>
                                  <td>{fmt(d['과표차이'])}</td>
                                  <td>{fmt(d['세액차이'])}</td>
                                </tr>
                              ))}
                              <tr style={{ fontWeight: 600 }}>
                                <td className="c">합계</td>
                                <td>{fmt(어긋난날.reduce((s, d) => s + d['과표차이'], 0))}</td>
                                <td>{fmt(어긋난날.reduce((s, d) => s + d['세액차이'], 0))}</td>
                              </tr>
                            </tbody>
                          </table>
                          </details>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                )
              })}
            </tbody>
          </table>
          <div className="row" style={{ justifyContent: 'flex-end', marginTop: 14 }}>
            <button type="button" className="btn" onClick={onGoLedger}>원장 보기 →</button>
          </div>
        </div>
      )}
    </>
  )
}

// 원장에서 만든 복식 분개. 저장하지 않고 조회할 때마다 서버가 다시 만든다 —
// 원장을 고치면 분개도 따라 바뀌어야 하기 때문.
function JournalPane({ month }) {
  const [defStart, defEnd] = monthRange(month)
  const [start, setStart] = useState(defStart)
  const [end, setEnd] = useState(defEnd)
  const [data, setData] = useState(null)
  const [err, setErr] = useState('')
  const [loading, setLoading] = useState(false)
  const [onlyIssues, setOnlyIssues] = useState(false)
  const [picking, setPicking] = useState(null) // {ledgerId, seq, lineNo, query}
  const [saving, setSaving] = useState(false)

  const load = () => {
    setErr(''); setLoading(true)
    portal.get(`/api/securities/journal?start=${start}&end=${end}`)
      .then(setData)
      .catch((e) => setErr(e.status === 403 ? '증권 화면 권한이 없습니다.' : e.message))
      .finally(() => setLoading(false))
  }

  const saveVendor = async (target, vendorCode, vendorName) => {
    setSaving(true)
    try {
      await portal.post('/api/securities/journal/vendor', {
        ledgerId: target.ledgerId, seq: target.seq, lineNo: target.lineNo, vendorCode, vendorName,
      })
      setPicking(null)
      load()
    } catch (e) { setErr(e.message) } finally { setSaving(false) }
  }

  useEffect(() => { const [s, e] = monthRange(month); setStart(s); setEnd(e) }, [month])

  useEffect(() => { load() }, [start, end])

  const vouchers = (data?.vouchers ?? []).filter(
    (v) => !onlyIssues || !v['대차일치'] || v.lines.some((l) => !l['거래처코드']),
  )

  return (
    <>
      <div className="row" style={{ marginBottom: 10 }}>
        <DateRange start={start} end={end} onChange={(s, e) => { setStart(s); setEnd(e) }} />
        {data && (
          <span className="muted">
            원장 {fmt(data.ledgerCount)}행 → 분개 {fmt(data.vouchers.length)}건
            {data.unbalanced > 0 && <span style={{ color: 'var(--danger)' }}> · 대차 불일치 {fmt(data.unbalanced)}건</span>}
            {data.missingVendor > 0 && <span style={{ color: 'var(--danger)' }}> · 거래처 미지정 {fmt(data.missingVendor)}줄</span>}
          </span>
        )}
        <label className="row" style={{ gap: 6 }}>
          <input type="checkbox" checked={onlyIssues} onChange={(e) => setOnlyIssues(e.target.checked)} />
          확인 필요만
        </label>
      </div>
      {err && <div className="error-box">{err}</div>}
      {!err && loading && <div className="muted">불러오는 중…</div>}
      {picking && (
        <VendorPickerModal
          initialQuery={picking.query}
          onSelect={(v) => saveVendor(picking, v.code, v.name)}
          onClose={() => setPicking(null)}
        />
      )}
      {!err && !loading && data && (
        <div className="panel">
          <table>
            <thead>
              <tr>
                <th>일자</th><th>차대</th><th>코드</th><th className="l">계정과목</th>
                <th>거래처코드</th><th className="l">거래처명</th><th>금액</th><th className="l">적요</th><th>외화</th>
              </tr>
            </thead>
            <tbody>
              {vouchers.length === 0 && (
                <tr><td colSpan={9} className="c muted">해당 기간 분개가 없습니다</td></tr>
              )}
              {vouchers.map((v, vi) => (
                <React.Fragment key={vi}>
                  {v.lines.length === 0 && (
                    <tr><td className="c">{v['일자']}</td><td colSpan={8} className="l" style={{ color: 'var(--danger)' }}>{v['적요']}</td></tr>
                  )}
                  {v.lines.map((l, li) => (
                    <tr key={li} style={li === 0 ? { borderTop: '2px solid var(--line-strong)' } : undefined}>
                      <td className="c">{li === 0 ? v['일자'] : ''}</td>
                      <td className="c" style={{ color: l['차대'] === '차' ? 'var(--accent)' : 'var(--muted)' }}>{l['차대']}</td>
                      <td className="c">{l['계정코드']}</td>
                      <td className="l">{l['계정명']}</td>
                      <td className="c">
                        <button type="button" className="link-cell"
                          style={l['거래처코드'] ? undefined : { color: 'var(--danger)' }}
                          disabled={v['원장ID'] == null || saving}
                          onClick={() => setPicking({
                            ledgerId: v['원장ID'], seq: v['순번'], lineNo: l['줄번호'],
                            query: l['거래처명'] || '',
                          })}>
                          {l['거래처코드'] || '미지정'}
                        </button>
                      </td>
                      <td className="l">
                        {l['거래처명']}
                        {l['거래처지정'] && <span className="muted" style={{ fontSize: 11 }}> · 지정</span>}
                      </td>
                      <td>{fmt(l['금액'])}</td>
                      <td className="l">{l['적요']}</td>
                      <td className="c muted">{l['외화금액'] ? `${fmt(l['외화금액'], 2)} ${l['통화']}` : ''}</td>
                    </tr>
                  ))}
                  {!v['대차일치'] && v.lines.length > 0 && (
                    <tr><td /><td colSpan={8} className="l" style={{ color: 'var(--danger)' }}>
                      대차 불일치 — 차변 {fmt(v['차변합계'])} / 대변 {fmt(v['대변합계'])}
                    </td></tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  )
}

// 보유 종목(MMT·RP·외화RP) — 계좌 화면처럼 원화·외화를 갈라 종목별로 본다.
//
// 한투는 거래가 있는 날만 잔고 스냅샷을 준다. 그래서 «오늘 잔고» 는 그날 이전의
// 마지막 스냅샷이고, 아래 추이는 스냅샷이 찍힌 날만 선다 - 빈 날은 그대로 이어진다.
function HoldingsPane({ month }) {
  const [defStart, defEnd] = monthRange(month)
  const [start, setStart] = useState(defStart)
  const [end, setEnd] = useState(defEnd)
  const [ccy, setCcy] = useState('KRW')
  const [data, setData] = useState(null)
  const [err, setErr] = useState('')

  useEffect(() => {
    setErr(''); setData(null)
    portal.get(`/api/securities/holdings?start=${start}&end=${end}`)
      .then(setData)
      .catch((e) => setErr(e.status === 403
        ? '증권 화면 권한이 없습니다.' : e.message))
  }, [start, end])

  if (err) return <div className="error-box">{err}</div>
  if (!data) return <div className="muted">불러오는 중…</div>

  const 통화들 = [...new Set([...(data.holdings || []).map((h) => h.통화),
                              ...(data.series || []).map((h) => h.통화)])]
  const 탭 = 통화들.includes(ccy) ? ccy : (통화들[0] || 'KRW')
  const 보유 = (data.holdings || []).filter((h) => h.통화 === 탭)
  const 추이 = (data.series || []).filter((h) => h.통화 === 탭)
  const 합계 = 보유.reduce((s, h) => s + (Number(h.잔고) || 0), 0)
  const 원화 = 탭 === 'KRW'

  return (
    <>
      <div className="row" style={{ marginBottom: 10, gap: 8, flexWrap: 'wrap' }}>
        <DateRange start={start} end={end} onChange={(a2, b2) => { setStart(a2); setEnd(b2) }} />
        {통화들.map((c) => (
          <button key={c} type="button"
            className={'btn ' + (탭 === c ? '' : 'secondary')}
            onClick={() => setCcy(c)}>{c === 'KRW' ? '원화' : c}</button>
        ))}
        {/* 한투는 수기 엑셀 업로드다. 어디까지 반영됐는지 모르면 «0원» 이 정말 0 인지
            아직 안 올라온 것인지 가릴 수 없다 */}
        <span className="muted" style={{ fontSize: 12, marginLeft: 'auto' }}>
          한투 자료 {data.dataRange?.last || '-'} 까지 반영
        </span>
      </div>

      <div className="panel">
        <h3 style={{ marginTop: 0 }}>
          {end} 기준 보유
          <span className="muted" style={{ fontSize: 12, fontWeight: 400 }}>
            {' '}· 합계 {fmt(원화 ? Math.round(합계) : Math.round(합계 * 100) / 100)}
            {원화 ? '원' : ` ${탭}`}
          </span>
        </h3>
        <table style={{ maxWidth: 760 }}>
          <thead><tr>
            <th style={{ width: 80 }}>잔고번호</th>
            <th className="l">종목명</th>
            <th style={{ width: 120 }}>기준일</th>
            <th style={{ width: 160 }}>잔고</th>
          </tr></thead>
          <tbody>
            {보유.length === 0 && (
              <tr><td colSpan={4} className="c muted">보유 종목이 없습니다</td></tr>
            )}
            {보유.map((h) => (
              <tr key={h.잔고번호} style={Number(h.잔고) ? undefined : { opacity: .5 }}>
                <td className="c muted" style={{ fontSize: 12 }}>{h.잔고번호}</td>
                <td className="l">{h.종목명}</td>
                <td className="c muted" style={{ fontSize: 12 }}>{h.일자}</td>
                <td>{fmt(원화 ? Math.round(h.잔고) : Math.round(h.잔고 * 100) / 100)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="muted" style={{ fontSize: 11, marginTop: 8 }}>
          잔고가 0인 종목도 함께 둡니다 — «들고 있던 것이 0이 됐다»와 «처음부터 없었다»는
          다른 이야기입니다.
        </div>
      </div>

      <div className="panel">
        <h3 style={{ marginTop: 0 }}>
          잔고가 바뀐 날
          <span className="muted" style={{ fontSize: 12, fontWeight: 400 }}>
            {' '}· 한투가 거래 있는 날만 스냅샷을 줍니다
          </span>
        </h3>
        <table style={{ maxWidth: 760 }}>
          <thead><tr>
            <th style={{ width: 120 }}>일자</th>
            <th className="l">종목명</th>
            <th style={{ width: 160 }}>잔고</th>
          </tr></thead>
          <tbody>
            {추이.length === 0 && (
              <tr><td colSpan={3} className="c muted">이 기간에 바뀐 잔고가 없습니다</td></tr>
            )}
            {추이.map((h, i) => (
              <tr key={`${h.일자}-${h.잔고번호}-${i}`}>
                <td className="c">{h.일자}</td>
                <td className="l">{h.종목명}
                  <span className="muted" style={{ fontSize: 11 }}> · {h.잔고번호}</span>
                </td>
                <td>{fmt(원화 ? Math.round(h.잔고) : Math.round(h.잔고 * 100) / 100)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  )
}

export default function Securities(props) {
  const [tab, setTab] = useState(() => {
    try { return localStorage.getItem('securities-tab') || 'upload' } catch { return 'upload' }
  })
  const [reloadKey, setReloadKey] = useState(0)

  const changeTab = (t) => {
    setTab(t)
    try { localStorage.setItem('securities-tab', t) } catch {}
  }

  return (
    <>
      <div className="data-tabs">
        {TABS.map((t) => (
          <button key={t.k} type="button"
            className={'data-tab' + (tab === t.k ? ' active' : '')}
            onClick={() => changeTab(t.k)}>{t.label}</button>
        ))}
      </div>
      {tab === 'upload' && (
        <>
          <h2>증권 · 업로드</h2>
          <UploadPane
            onUploaded={() => setReloadKey((k) => k + 1)}
            onGoLedger={() => changeTab('ledger')} />
        </>
      )}
      {tab === 'ledger' && <SourceData key={reloadKey} kind="securities" {...props} />}
      {tab === 'holdings' && (
        <>
          <h2>증권 · 보유 종목</h2>
          <div className="hint" style={{ marginBottom: 10 }}>
            예수금에서 MMT·RP 로 옮긴 것은 돈이 나간 게 아니라 자리만 바뀐 것입니다.
            원장에는 출금으로만 찍혀 밖으로 나간 것처럼 보이므로, 잔고를 같이 봐야 갈립니다.
          </div>
          <HoldingsPane month={props.month} />
        </>
      )}
      {tab === 'journal' && (
        <>
          <h2>증권 · 분개</h2>
          <div className="hint" style={{ marginBottom: 10 }}>
            원장에서 자동 생성한 복식 분개입니다. 타사이체는 상대 계좌 코드가 원장에 없어 거래처가 비어 있습니다 —
            “확인 필요만”으로 걸러서 채우시면 됩니다.
          </div>
          <JournalPane month={props.month} />
        </>
      )}
    </>
  )
}
